import { Auth0ContextInterface } from "@auth0/auth0-react";
import ConversationDomain from "entities/domain/conversations/conversation-domain";
import {
  ConversationSortOrder,
  ConversationTab,
  OpenClosedFilter,
  SearchField,
} from "redux/features/conversations";
import InboxService from "services/inbox";
import { InboxScope, serializeInboxScope } from "util/inbox-scope";

// Conversations-list query key and page fetcher, kept in their own module so
// the eager app shell (usePrefetchConversations in PrivateRoute) can import
// them without dragging use-conversations' other dependencies (templates and
// reply-suggestion services) into the startup bundle graph.

export interface ConversationsListParams {
  merchantId: number;
  tab: ConversationTab;
  status: OpenClosedFilter;
  searchText: string;
  searchField: SearchField;
  offset: number;
  channels?: string[];
  agents?: string[];
  tagIds?: string[];
  inboxScope: InboxScope;
  sentiment?: string | null;
  urgency?: string | null;
  sortOrder?: ConversationSortOrder;
}

export const conversationQueryKey = (
  merchantId: number | null | undefined,
  conversationId: number | null | undefined
) => ["conversations", "detail", merchantId ?? null, conversationId ?? null];

export const conversationMessagesQueryKey = (
  merchantId: number | null | undefined,
  conversationId: number | null | undefined
) => ["conversations", "messages", merchantId ?? null, conversationId ?? null];

/**
 * The list key's exact shape. conversations-cache.ts mirrors it positionally to
 * decide socket-update membership, and rejects any key of a different length —
 * silently, since the key arrives there as `unknown`. Exporting the tuple and
 * its length means adding a field breaks the build instead of the inbox.
 */
export type ConversationsListQueryKey = [
  "conversations",
  "list",
  number | null,
  ConversationTab,
  OpenClosedFilter,
  string,
  SearchField,
  string[] | null,
  string[] | null,
  string[] | null,
  string,
  string | null,
  string | null,
  ConversationSortOrder | null,
];

export const CONVERSATIONS_LIST_QUERY_KEY_LENGTH = 14;

export const conversationsListQueryKey = (
  merchantId: number | null | undefined,
  params: Omit<ConversationsListParams, "merchantId" | "offset">
): ConversationsListQueryKey => {
  const sortedChannels = params.channels
    ? [...params.channels].sort()
    : undefined;
  const sortedAgents = params.agents ? [...params.agents].sort() : undefined;
  const sortedTagIds = params.tagIds ? [...params.tagIds].sort() : undefined;

  return [
    "conversations",
    "list",
    merchantId ?? null,
    params.tab,
    params.status,
    params.searchText,
    params.searchField,
    sortedChannels ?? null,
    sortedAgents ?? null,
    sortedTagIds ?? null,
    // Serialized rather than the object itself: react-query hashes keys
    // structurally, but conversations-cache.ts reads this slot positionally to
    // decide list membership for socket updates, and a string is what it can
    // validate. `all` and `inbox(x)` are distinct values, so they can never
    // share a cache entry.
    serializeInboxScope(params.inboxScope),
    params.sentiment ?? null,
    params.urgency ?? null,
    params.sortOrder ?? null,
  ];
};

// Shared page fetcher so the inbox list query and the early prefetch are
// guaranteed to hit the same endpoint with the same arguments.
export const fetchConversationsListPage = async (
  auth0Context: Auth0ContextInterface,
  merchantId: number | undefined,
  params: Omit<ConversationsListParams, "merchantId" | "offset">,
  pageParam: number,
  signal?: AbortSignal
): Promise<ConversationDomain[]> => {
  if (!merchantId) return [];

  const statusString =
    params.status === OpenClosedFilter.Open ? "open" : "closed";

  if (params.tab === ConversationTab.Team) {
    return InboxService.getConversations(
      auth0Context,
      statusString,
      params.searchText,
      params.searchField,
      pageParam,
      merchantId,
      params.channels,
      params.agents,
      params.tagIds,
      params.inboxScope,
      params.sentiment,
      params.urgency,
      params.sortOrder,
      signal
    );
  } else if (params.tab === ConversationTab.Personal) {
    return InboxService.getPersonalConversations(
      auth0Context,
      statusString,
      params.searchText,
      params.searchField,
      pageParam,
      merchantId,
      params.channels,
      params.tagIds,
      params.inboxScope,
      params.sentiment,
      params.urgency,
      params.sortOrder,
      signal
    );
  } else if (params.tab === ConversationTab.Unassigned) {
    return InboxService.getUnassignedConversations(
      auth0Context,
      statusString,
      params.searchText,
      params.searchField,
      pageParam,
      merchantId,
      params.channels,
      params.tagIds,
      params.inboxScope,
      params.sentiment,
      params.urgency,
      params.sortOrder,
      signal
    );
  }

  return [];
};
